import { useCallback, useMemo } from 'react';
import { useIoContext } from '~/contexts';
import { useServiceBillingDetails } from '~/hooks/reactQuery/queries/useServiceBillingDetails';
import { useLocaleKeys } from '~/i18n';
import { format, isAfterOrEqual, isBefore, startOfDay } from '~/infra/utils/dates-fns';
import { serviceModel } from '~/models';
import { StartAutomaticBillingAtControlDropdownMenuItem } from '../StartAutomaticBillingAtControl';
export const useCallOuts = (param)=>{
    let { testIds, relationshipEffectiveDate, service, selectedItem, isChecked, isAmendedService } = param;
    const { StartAutomaticBillingAtControlTestIds } = testIds;
    const { getNow } = useIoContext();
    const { t } = useLocaleKeys('components.serviceAdvancedSettings.startAutomaticBillingAtControl.callouts');
    const getToday = useCallback(()=>startOfDay(getNow()), [
        getNow
    ]);
    const isProrated = serviceModel.isProrated(service);
    const isBilledUpfront = serviceModel.isBilledUpfront(service);
    const { nextBillingDate, periods } = useServiceBillingDetails({
        service,
        relationshipEffectiveDate: relationshipEffectiveDate
    });
    const startBillingAt = serviceModel.startBillingAt(service);
    const shouldShowProrateWarning = useMemo(()=>{
        if (!isProrated && isBilledUpfront && startBillingAt && nextBillingDate) {
            return nextBillingDate.getTime() !== startBillingAt.getTime();
        }
        return false;
    }, [
        nextBillingDate,
        startBillingAt,
        isProrated,
        isBilledUpfront
    ]);
    return useMemo(()=>{
        const _callouts = [];
        if (!isChecked) {
            return _callouts;
        }
        if (selectedItem === StartAutomaticBillingAtControlDropdownMenuItem.SpecificDate && startBillingAt) {
            const formattedDate = format(startBillingAt, 'MMM d, yyyy');
            if (!isAmendedService && isAfterOrEqual(startBillingAt, getToday())) {
                if (relationshipEffectiveDate) {
                    _callouts.push({
                        dataTestId: StartAutomaticBillingAtControlTestIds.Callouts.FutureDate(),
                        text: t.futureDate({
                            date: formattedDate
                        })
                    });
                } else {
                    _callouts.push({
                        dataTestId: StartAutomaticBillingAtControlTestIds.Callouts.OnAcceptanceDate(),
                        text: t.onAcceptanceDate({
                            date: formattedDate
                        })
                    });
                }
            }
            if (shouldShowProrateWarning) {
                _callouts.push({
                    dataTestId: StartAutomaticBillingAtControlTestIds.Callouts.Proration(),
                    text: t.proration()
                });
            }
            if (periods && periods > 0 && isBefore(startBillingAt, getToday())) {
                _callouts.push({
                    dataTestId: StartAutomaticBillingAtControlTestIds.Callouts.PriorPeriods(),
                    text: t.priorPeriods({
                        periods,
                        date: formattedDate
                    })
                });
            }
        }
        return _callouts;
    }, [
        selectedItem,
        isChecked,
        startBillingAt,
        shouldShowProrateWarning,
        periods,
        relationshipEffectiveDate,
        isAmendedService,
        t,
        StartAutomaticBillingAtControlTestIds.Callouts,
        getToday
    ]);
};
